import { GlobalOutlined } from '@ant-design/icons';
import { Button, Dropdown, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';
import type { MenuProps } from 'antd';

const LANGUAGE_CACHE_KEY = 'aspice_language';

interface LanguageSwitchProps {
  /** 显示模式: 'button' | 'icon' | 'text' */
  mode?: 'button' | 'icon' | 'text';
  /** 是否显示当前语言文本 */
  showText?: boolean;
  /** 自定义样式类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
}

const LanguageSwitch: React.FC<LanguageSwitchProps> = ({
  mode = 'icon',
  showText = false,
  className,
  style,
}) => {
  const { i18n } = useTranslation();
  const { setLocale } = useLocale();
  const [currentLocale, setCurrentLocale] = useState(i18n.language);

  // 语言选项配置
  const languages = [
    {
      key: 'zh-CN',
      label: '中文',
      icon: '/flags/cn.svg',
    },
    {
      key: 'en-US',
      label: 'English',
      icon: '/flags/us.svg',
    },
  ];

  // 获取浏览器语言
  const getBrowserLanguage = (): string => {
    const browserLang = navigator.language || navigator.languages?.[0] || 'zh-CN';
    // 映射浏览器语言到应用支持的语言
    if (browserLang.startsWith('en')) {
      return 'en-US';
    }
    return 'zh-CN';
  };

  // 初始化语言设置
  useEffect(() => {
    const initLanguage = () => {
      // 1. 首先检查缓存中的语言设置
      const cachedLanguage = localStorage.getItem(LANGUAGE_CACHE_KEY);
      if (cachedLanguage && languages.find((lang) => lang.key === cachedLanguage)) {
        if (cachedLanguage !== currentLocale) {
          i18n.changeLanguage(cachedLanguage);
          setLocale(cachedLanguage);
          setCurrentLocale(cachedLanguage);
        }
        return;
      }

      // 2. 如果缓存中没有，使用浏览器语言
      const browserLanguage = getBrowserLanguage();
      if (browserLanguage !== currentLocale) {
        i18n.changeLanguage(browserLanguage);
        setLocale(browserLanguage);
        setCurrentLocale(browserLanguage);
        localStorage.setItem(LANGUAGE_CACHE_KEY, browserLanguage);
      }
    };

    initLanguage();
  }, []);

  // 切换语言
  const handleLanguageChange = (locale: string) => {
    if (locale !== currentLocale) {
      i18n.changeLanguage(locale);
      setLocale(locale);
      setCurrentLocale(locale);
      localStorage.setItem(LANGUAGE_CACHE_KEY, locale);
    }
  };

  // 构建下拉菜单项
  const menuItems: MenuProps['items'] = languages.map((lang) => ({
    key: lang.key,
    label: (
      <Space>
        <img
          src={lang.icon}
          alt={lang.label}
          style={{ width: '16px', height: '12px', objectFit: 'cover' }}
        />
        <span>{lang.label}</span>
        {currentLocale === lang.key && <span style={{ color: '#1890ff' }}>✓</span>}
      </Space>
    ),
    onClick: () => handleLanguageChange(lang.key),
  }));

  // 获取当前语言信息
  const currentLanguage = languages.find((lang) => lang.key === currentLocale) || languages[0];

  // 根据模式渲染不同的触发器
  const renderTrigger = () => {
    switch (mode) {
      case 'button':
        return (
          <Button icon={<GlobalOutlined />} className={className} style={style}>
            {showText && currentLanguage.label}
          </Button>
        );

      case 'text':
        return (
          <span className={className} style={{ cursor: 'pointer', ...style }}>
            <Space>
              <GlobalOutlined />
              {currentLanguage.label}
            </Space>
          </span>
        );

      case 'icon':
      default:
        return (
          <div
            className={className}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              height: '48px',
              padding: '0 12px',
              transition: 'all 0.3s',
              ...style,
            }}
          >
            <Space>
              <GlobalOutlined style={{ fontSize: '16px' }} />
              {showText && <span>{currentLanguage.label}</span>}
            </Space>
          </div>
        );
    }
  };

  return (
    <Dropdown
      menu={{ items: menuItems }}
      placement="bottomRight"
      trigger={['click']}
      overlayStyle={{ minWidth: '120px' }}
    >
      {renderTrigger()}
    </Dropdown>
  );
};

export default LanguageSwitch;
